/**
 * Get Single Product Details
 *
 * Fetches detailed product information from NBC API for a single product.
 * Used on offer page to display room types, images, and descriptions.
 *
 * @module services/api/nbc/getProduct
 */

import { nbcClient } from './nbcClient';
import { NBC_ENDPOINTS } from './config';
import { NBCSingleProductResponseSchema, type NBCSingleProductResponse } from '@/types/schemas/nbc';

/**
 * Fetch single product details from NBC API
 *
 * Returns detailed product information including room types, images,
 * tags, and HTML content (highlights, description, service, terms).
 *
 * @param productId - Product ID from Player API
 * @returns Product details with room types and images
 *
 * @throws {NetworkError} If request times out or network fails
 * @throws {ValidationError} If response doesn't match schema
 * @throws {PlayerApiError} For other API errors (4xx, 5xx)
 *
 * @example Basic usage
 * const product = await getProduct('3123123');
 *
 * if (product.roomTypes.length > 0) {
 *   console.log('Room types:', product.roomTypes);
 * }
 *
 * @example With error handling
 * try {
 *   const product = await getProduct('3123123');
 *   // Use NBC data
 * } catch (error) {
 *   if (error instanceof NetworkError) {
 *     console.warn('NBC unavailable, using Player data only');
 *     // Continue without NBC enrichment
 *   } else {
 *     throw error;
 *   }
 * }
 *
 * @example Accessing room types
 * const product = await getProduct('3123123');
 *
 * product.roomTypes.forEach((roomType) => {
 *   console.log(`${roomType.roomTypeName} (${roomType.roomTypeCode})`);
 *   roomType.roomTypeImages.forEach((image) => {
 *     console.log(`  - ${image.caption || 'No caption'}`);
 *   });
 * });
 */
export async function getProduct(productId: string): Promise<NBCSingleProductResponse> {
  return nbcClient.get<NBCSingleProductResponse>({
    endpoint: NBC_ENDPOINTS.PRODUCT(productId),
    responseSchema: NBCSingleProductResponseSchema,
  });
}
